import type { AircraftSectionAcceptedSceneBinding } from "./aircraftSectionBindingApi";
import type { AircraftSectionTarget } from "./aircraftSectionExtractionApi";
import type { LoomVerifiedAircraftOccurrenceAsset } from "./loomAircraftOccurrenceAsset";
import type { LoomVerifiedAircraftTransitionAsset } from "./loomAircraftTransitionAsset";
import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
export type { AircraftSectionAcceptedSceneBinding } from "./aircraftSectionBindingApi";

type LoomVerifiedAcceptedAssemblyAsset = LoomVerifiedAircraftOccurrenceAsset
  | LoomVerifiedAircraftTransitionAsset;

export function loomAcceptedSectionTargetKind(
  document: LoomAuthoringDocument,
  nodeId: string,
): AircraftSectionTarget["kind"] | null {
  const byId = new Map(document.nodes.map((node) => [node.id, node]));
  const visited = new Set<string>();
  let node = byId.get(nodeId);
  while (node?.type === "transform" && !visited.has(node.id)) {
    visited.add(node.id);
    node = byId.get(node.inputNodeId);
  }
  if (node?.type !== "primitive") return null;
  if (node.definition.primitive === "wing") return "wing";
  return node.definition.primitive === "fuselage" ? "body" : null;
}

export function deriveLoomAcceptedSceneBinding(
  asset: LoomNativeHybridAsset | null,
  geometryCurrent: boolean,
  previewActive: boolean,
): AircraftSectionAcceptedSceneBinding | null {
  if (!asset || asset.integrity?.status !== "verified" || !geometryCurrent || previewActive) {
    return null;
  }
  // The legacy v1 package has no semantic definition receipt. Its verified
  // whole-container digest is the exact source-package authority; newer
  // packages retain their verified semantic definition revision.
  const sourceDefinitionRevision = asset.header.parameters.definitionRevision
    ?? asset.integrity.sourceContainerRevision;
  return {
    sourceDefinitionRevision,
    acceptedGeometryRevision: asset.acceptedGeometry.header.geometryRevision,
    acceptedTopologyRevision: asset.acceptedGeometry.header.topologyRevision,
    payloadRevision: `sha256:${asset.header.payloadSha256}`,
    poseApplication: "identity-root/v1",
  };
}

export function deriveLoomAcceptedAssemblySceneBinding(
  asset: LoomVerifiedAcceptedAssemblyAsset | null,
  geometryCurrent: boolean,
  previewActive: boolean,
): AircraftSectionAcceptedSceneBinding | null {
  if (!asset || asset.integrity?.status !== "verified" || !geometryCurrent || previewActive) {
    return null;
  }
  return {
    sourceDefinitionRevision: asset.header.definitionBinding.definitionRevision,
    acceptedGeometryRevision: asset.header.surface.geometryRevision,
    acceptedTopologyRevision: asset.header.surface.topologyRevision,
    payloadRevision: asset.header.payloadSha256,
    poseApplication: "identity-root/v1",
  };
}
